/**
 * Project Manager Component
 *
 * Displays a list of projects with CRUD operations.
 * Uses ProjectEditor component for create/edit wizard.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Upload as UploadIcon,
  People as PeopleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import api, { DatasetUploadResult } from '../services/api';
import DataUpload from './DataUpload';
import ProjectUsers from './ProjectUsers';
import ProjectEditor from './ProjectEditor';

interface Project {
  id: string;
  tenant_id: string;
  project_name: string;
  project_slug: string;
  subdomain?: string;
  subdomain_status?: 'pending' | 'approved' | 'active' | 'suspended';
  organization_name?: string;
  city_name: string;
  status: string;
  cpu_cores?: number;
  ram_gb?: number;
  created_at: string;
  deployment_url?: string;
}

interface Tenant {
  id: string;
  tenant_id: string;
  organization_name: string;
}

const ProjectManager: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Manage users state
  const [manageUsersProject, setManageUsersProject] = useState<Project | null>(null);

  // Project editor state
  const [projectEditorOpen, setProjectEditorOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);

  // Load projects and tenants
  const loadProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/projects');
      setProjects(response.data.projects || response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data.tenants || response.data || []);
    } catch (err: any) {
      console.error('Failed to load tenants:', err);
    }
  };

  useEffect(() => {
    loadProjects();
    loadTenants();
  }, []);

  const handleDeleteProject = async (projectId: string) => {
    if (!window.confirm('Are you sure? This cannot be undone.')) return;

    setLoading(true);
    setError(null);
    try {
      await api.delete(`/projects/${projectId}`);
      setSuccess('Project deleted successfully');
      await loadProjects();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete project');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = async (project: Project) => {
    // Open the new ProjectEditor dialog instead of the old edit dialog
    setEditingProjectId(project.id);
    setProjectEditorOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'deploying': return 'warning';
      case 'pending': return 'default';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <ActiveIcon fontSize="small" />;
      case 'deploying': return <CircularProgress size={16} />;
      case 'pending': return <PendingIcon fontSize="small" />;
      case 'error': return <ErrorIcon fontSize="small" />;
      default: return undefined;
    }
  };

  // Handle upload dialog
  const handleOpenUpload = (project: Project) => {
    setSelectedProject(project);
    setUploadDialogOpen(true);
  };

  const handleCloseUpload = () => {
    setUploadDialogOpen(false);
    setSelectedProject(null);
  };

  const handleUploadComplete = (result: DatasetUploadResult) => {
    setSuccess(`Dataset uploaded successfully to ${selectedProject?.project_name}`);
    handleCloseUpload();
  };

  return (
    <Box>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Projects contain full configuration: resources, models, components, and city data.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadProjects}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            disabled={tenants.length === 0}
          >
            Create Project
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {tenants.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No organizations found. Create an organization first.
        </Alert>
      )}

      {/* Projects Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Project Name</TableCell>
              <TableCell>Subdomain</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Resources</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary">No projects found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              projects.map(project => (
                <TableRow key={project.id}>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(project.status)}
                      label={project.status}
                      color={getStatusColor(project.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{project.project_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{project.project_slug}</Typography>
                  </TableCell>
                  <TableCell>
                    {(project.deployment_url || project.subdomain) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={`Open ${project.deployment_url || `https://${project.subdomain}.gus.silacities.com`}`}>
                          <Chip
                            label={project.subdomain || new URL(project.deployment_url || '').hostname}
                            size="small"
                            color={project.subdomain_status === 'active' ? 'success' : project.subdomain_status === 'approved' ? 'info' : 'default'}
                            onClick={() => window.open(project.deployment_url || `https://${project.subdomain}.gus.silacities.com`, '_blank')}
                            onDelete={() => window.open(project.deployment_url || `https://${project.subdomain}.gus.silacities.com`, '_blank')}
                            deleteIcon={<OpenInNewIcon fontSize="small" />}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>{project.organization_name || '-'}</TableCell>
                  <TableCell>{project.city_name}</TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      {project.cpu_cores || 2} cores / {project.ram_gb || 4} GB
                    </Typography>
                  </TableCell>
                  <TableCell>{new Date(project.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title="Manage Users">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => setManageUsersProject(project)}
                      >
                        <PeopleIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Upload Data">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleOpenUpload(project)}
                      >
                        <UploadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditProject(project)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteProject(project.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Project Dialog - Using unified ProjectEditor */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <ProjectEditor
            onClose={() => setCreateDialogOpen(false)}
            onSaved={() => {
              setCreateDialogOpen(false);
              setSuccess('Project created successfully');
              loadProjects();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Upload Data Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUpload}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Upload Data to {selectedProject?.project_name}
        </DialogTitle>
        <DialogContent>
          {selectedProject && (
            <DataUpload
              tenantId={selectedProject.tenant_id}
              projectId={selectedProject.id}
              onUploadComplete={handleUploadComplete}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpload}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog - Using ProjectEditor */}
      <Dialog
        open={projectEditorOpen}
        onClose={() => { setProjectEditorOpen(false); setEditingProjectId(null); }}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {editingProjectId && (
            <ProjectEditor
              projectId={editingProjectId}
              onClose={() => { setProjectEditorOpen(false); setEditingProjectId(null); }}
              onSaved={() => {
                setProjectEditorOpen(false);
                setEditingProjectId(null);
                setSuccess('Project updated successfully');
                loadProjects();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Project Users Dialog */}
      <Dialog
        open={!!manageUsersProject}
        onClose={() => setManageUsersProject(null)}
        maxWidth="lg"
        fullWidth
      >
        {manageUsersProject && (
          <ProjectUsers
            projectId={manageUsersProject.id}
            projectName={manageUsersProject.project_name}
            onBack={() => setManageUsersProject(null)}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default ProjectManager;
